import type { Locale } from "@borealis/layout-express";
import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { useMemo } from "react";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "../../utils/pulse-tracking/usePulse.ts";
import { ExpandableSection } from "./expandable-section/ExpandableSection.tsx";

type Props = {
  equipment?: IAdViewManaged[] | string;
};

export const Equipment = ({ equipment }: Props) => {
  const { i18n } = useLingui();
  const title = i18n.t("itemPageLayout.equipment.title", { message: "Equipment" });
  const { locale } = useAppConfig();
  if (!equipment) {
    return null;
  }
  if (typeof equipment === "string") {
    return <UnsafeEquipment equipment={equipment} title={title} />;
  }
  return <SafeEquipment equipment={equipment} title={title} locale={locale} />;
};

type SafeProps = {
  equipment?: IAdViewManaged[];
  title: string;
  locale: Locale;
};

const SafeEquipment = ({ equipment = [], title, locale }: SafeProps) => {
  const { brand } = useAppConfig();
  const sortedEquipment = useMemo(
    () =>
      brand.toLocaleLowerCase() === "blocket"
        ? equipment
        : [...equipment].sort((a, b) => a.value.localeCompare(b.value, locale)),
    [equipment, locale, brand],
  );

  if (equipment.length === 0) {
    return null;
  }
  return (
    <section className="border-t pt-40 mt-40">
      <h2 className="t3 mb-0">{title}</h2>
      <ul className="columns-2 md:columns-3 break-words mb-0 mt-16">
        {sortedEquipment.map((e) => (
          <li className="last:mb-0 mb-8 md:mb-16" key={e.id ?? e.value}>
            {e.value}
          </li>
        ))}
      </ul>
    </section>
  );
};

type UnsafeProps = {
  equipment?: string;
  title: string;
};

const UnsafeEquipment = ({ equipment, title }: UnsafeProps) => {
  const { trackClick } = usePulse();
  const { i18n } = useLingui();

  const trackingEvent = () => {
    // Tracking event: CE_ev38
    trackClick({
      name: "View full ad",
      objectId: "CE_ev38",
      elementType: ElementType.Button,
      intent: Intent.View,
    });
  };

  if (!equipment || equipment?.length === 0) {
    return null;
  }

  return (
    <ExpandableSection
      headerText={title}
      bodyText={equipment}
      className="border-t mt-40 pt-40"
      trackingEvent={trackingEvent}
      readMoreText={i18n.t("itemPageLayout.equipment.readmore", {
        message: "Show entire equipment list",
        comment: "Button text to show full equipment list of expandable section",
      })}
    />
  );
};
